import type { DemoStep, DrillStep, ExplainStep, Lesson } from '../schema.js';

function explain(title: string, body: string, narration: string): ExplainStep {
  return { kind: 'explain', title, body, narration };
}

function demo(fen: string, from: string, to: string, body: string, check = false): DemoStep {
  return { kind: 'demo', title: 'See it happen', body, fen, highlight: { from, to, check } };
}

function drill(fen: string, task: DrillStep['task'], body: string, hint: string, answer: string[]): DrillStep {
  return {
    kind: 'drill',
    task,
    title: 'Your turn',
    body,
    fen,
    hint,
    answer,
  };
}

export const RULES_LESSONS: Lesson[] = [
  {
    id: 'check',
    slug: 'rules-check',
    title: 'Check — watch out, your king!',
    audienceLevel: 'beginner',
    concepts: ['rules'],
    steps: [
      explain(
        'What is check?',
        'Check means your king is attacked and must be saved this very turn. You can move away, block, or capture the attacker.',
        'When your king is attacked, you must get out of check right away.',
      ),
      demo('4k3/8/8/8/8/8/4R3/3K4 b - - 0 1', 'e2', 'e8', 'The rook gives check to the black king. Black must respond!', true),
      drill(
        '4k3/8/8/8/8/8/4r3/4K3 w - - 0 1',
        'best-move',
        'Your king is in check from the black rook. Get out of check!',
        'Capture the rook with your king.',
        ['Kxe2'],
      ),
    ],
  },
  {
    id: 'checkmate',
    slug: 'rules-checkmate',
    title: 'Checkmate — the game is won!',
    audienceLevel: 'beginner',
    concepts: ['rules'],
    steps: [
      explain(
        'What is checkmate?',
        'When a king is in check and has NO way to escape, that is checkmate — the game is over and the attacker wins.',
        'Checkmate is when the king cannot escape check.',
      ),
      demo('rnb1kbnr/pppp1ppp/8/4p3/6Pq/5P2/PPPPP2P/RNBQKBNR w KQkq - 1 3', 'd8', 'h4', 'The black queen delivers a surprise checkmate in just two moves — the fool’s mate!', true),
      drill(
        '7k/5K2/8/8/8/8/8/6Q1 w - - 0 1',
        'best-move',
        'Deliver checkmate with your queen!',
        'Move your queen all the way up the g-file to the last rank.',
        ['Qg7#', 'Qg8#', 'Qh1#', 'Qh2#'],
      ),
    ],
  },
  {
    id: 'stalemate',
    slug: 'rules-stalemate',
    title: 'Stalemate — a strange draw',
    audienceLevel: 'beginner',
    concepts: ['rules'],
    steps: [
      explain(
        'What is stalemate?',
        'If a player is NOT in check but has NO legal move at all, the game is a stalemate — a draw. No one wins, even if the other side has more pieces.',
        'Stalemate means the player to move has no legal moves but is not in check.',
      ),
      {
        kind: 'demo',
        title: 'A trapped king',
        body: 'The black king has no legal move, but it is NOT in check. That is stalemate — a draw!',
        fen: 'k7/2Q5/8/8/8/8/8/K7 b - - 0 1',
      },
      {
        kind: 'quiz',
        title: 'Stalemate check',
        body: 'A quick check on stalemate.',
        questions: [
          {
            prompt: 'You are NOT in check but you have NO legal moves. What happens?',
            choices: ['You lose', 'It is a stalemate — a draw', 'You may pass your turn'],
            correct: 1,
          },
          {
            prompt: 'Can a king in stalemate skip its turn and keep playing?',
            choices: ['Yes, skip the turn', 'No — the game is a draw'],
            correct: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'castling',
    slug: 'rules-castling',
    title: 'Castling — a super move',
    audienceLevel: 'beginner',
    concepts: ['rules'],
    steps: [
      explain(
        'What is castling?',
        'Castling moves your king two squares toward a rook, and the rook jumps over to the king’s other side. You may castle only if neither piece has moved and the king is not in or crossing check.',
        'Castling is a special move with your king and rook.',
      ),
      demo('r3k2r/8/8/8/8/8/8/R3K2R w KQkq - 0 1', 'e1', 'g1', 'The white king castles kingside — the rook hops over to f1.'),
      drill(
        'r3k2r/8/8/8/8/8/8/R3K2R w KQkq - 0 1',
        'best-move',
        'Castle to keep your king safe!',
        'Move your king two squares toward the kingside rook.',
        ['O-O'],
      ),
    ],
  },
  {
    id: 'en-passant',
    slug: 'rules-en-passant',
    title: 'En passant — in passing',
    audienceLevel: 'beginner',
    concepts: ['rules'],
    steps: [
      explain(
        'What is en passant?',
        'When a pawn moves two squares and lands beside an enemy pawn, that enemy pawn may capture it as if it had moved only one square. This capture is called en passant.',
        'En passant is a special pawn capture in passing.',
      ),
      demo('4k3/8/8/3pP3/8/8/8/4K3 w - d6 0 1', 'e5', 'd6', 'The white pawn captures the black pawn on d6 — en passant!'),
      drill(
        '4k3/8/8/3pP3/8/8/8/4K3 w - d6 0 1',
        'best-move',
        'Capture the black pawn with en passant!',
        'Your e5 pawn can capture the black pawn that just moved two squares.',
        ['exd6'],
      ),
    ],
  },
  {
    id: 'promotion',
    slug: 'rules-promotion',
    title: 'Promotion — a pawn becomes a queen',
    audienceLevel: 'beginner',
    concepts: ['rules'],
    steps: [
      explain(
        'What is promotion?',
        'When a pawn reaches the far side of the board, it is promoted — it becomes a queen, rook, bishop, or knight. A queen is almost always the best choice!',
        'A pawn that reaches the last rank turns into a queen — or another piece.',
      ),
      demo('4k3/P7/8/8/8/8/8/4K3 w - - 0 1', 'a7', 'a8', 'The white pawn marches to the last rank and becomes a queen!'),
      drill(
        '4k3/P7/8/8/8/8/8/4K3 w - - 0 1',
        'best-move',
        'March your pawn to the end and promote to a queen!',
        'Move your a7 pawn to the last rank and choose the queen.',
        ['a8=Q+'],
      ),
    ],
  },
];
